/**
 * Page Renderers for InvoAIce Platform
 * Each function renders a specific page's HTML
 */

import { t, getCurrentLanguage, getCurrency } from '../i18n/translations.js';
import { apiClient } from '../services/api.js';

/**
 * Dashboard stats state
 */
const dashboardState = {
  activeContracts: null,
  invoicesProcessed: null,
  openDiscrepancies: null,
  savingsIdentified: null,
  optimizationOpportunities: [],
  renewals: [],
  activities: [],
  discrepancies: [],
  loading: true,
};

/**
 * Fetch real dashboard stats from API
 * Uses incremental updates - each API call updates UI immediately when it completes
 */
async function fetchDashboardStats() {
  // Fire all requests in parallel but update UI incrementally as each completes
  // This makes the dashboard feel faster since stats appear as soon as they're available

  // Contracts count - update immediately when done
  apiClient.get('/v1/contracts?limit=1&status=active').then(contractsResponse => {
    if (contractsResponse?.data?.pageInfo?.totalItems !== undefined) {
      dashboardState.activeContracts = contractsResponse.data.pageInfo.totalItems;
    } else if (contractsResponse?.pageInfo?.totalItems !== undefined) {
      dashboardState.activeContracts = contractsResponse.pageInfo.totalItems;
    } else if (contractsResponse?.total !== undefined) {
      dashboardState.activeContracts = contractsResponse.total;
    }
    updateDashboardStats();
  }).catch(() => {});

  // Invoices count - update immediately when done
  apiClient.get('/v1/invoices?limit=1').then(invoicesResponse => {
    if (invoicesResponse?.data?.pageInfo?.totalItems !== undefined) {
      dashboardState.invoicesProcessed = invoicesResponse.data.pageInfo.totalItems;
    } else if (invoicesResponse?.pageInfo?.totalItems !== undefined) {
      dashboardState.invoicesProcessed = invoicesResponse.pageInfo.totalItems;
    } else if (invoicesResponse?.total !== undefined) {
      dashboardState.invoicesProcessed = invoicesResponse.total;
    }
    updateDashboardStats();
  }).catch(() => {});

  // Renewals - update immediately when done
  apiClient.get('/v1/renewals?daysAhead=90').then(renewalsResponse => {
    if (renewalsResponse) {
      let renewalsData = renewalsResponse.data || renewalsResponse;
      if (renewalsData && renewalsData.data !== undefined) {
        renewalsData = renewalsData.data;
      }
      dashboardState.renewals = Array.isArray(renewalsData) ? renewalsData : [];
      updateDashboardStats();
    }
  }).catch(() => {});

  // Activities - update immediately when done
  apiClient.get('/v1/audit/recent?limit=5').then(activitiesResponse => {
    if (activitiesResponse) {
      let activitiesData = activitiesResponse.data || activitiesResponse;
      if (activitiesData && activitiesData.data !== undefined) {
        activitiesData = activitiesData.data;
      }
      dashboardState.activities = Array.isArray(activitiesData) ? activitiesData : [];
      updateDashboardStats();
    }
  }).catch(() => {});

  // Discrepancies - update immediately when done
  apiClient.get('/v1/discrepancies?pageSize=5').then(discrepanciesResponse => {
    if (discrepanciesResponse) {
      let responseData = discrepanciesResponse.data || discrepanciesResponse;

      // Extract total count from pageInfo
      if (responseData?.pageInfo?.totalItems !== undefined) {
        dashboardState.openDiscrepancies = responseData.pageInfo.totalItems;
      } else if (responseData?.data?.pageInfo?.totalItems !== undefined) {
        dashboardState.openDiscrepancies = responseData.data.pageInfo.totalItems;
      }

      // Handle nested data structure
      let discrepanciesData = responseData;
      if (discrepanciesData && Array.isArray(discrepanciesData.data)) {
        discrepanciesData = discrepanciesData.data;
      } else if (discrepanciesData && discrepanciesData.items !== undefined) {
        discrepanciesData = discrepanciesData.items;
      }
      dashboardState.discrepancies = Array.isArray(discrepanciesData) ? discrepanciesData : [];

      // Fallback: use array length if no pageInfo
      if (dashboardState.openDiscrepancies === null && dashboardState.discrepancies.length > 0) {
        dashboardState.openDiscrepancies = dashboardState.discrepancies.length;
      }
      updateDashboardStats();
    }
  }).catch(() => {});

  // Optimization opportunities - update immediately when done
  apiClient.get('/v1/optimization/opportunities').then(optimizationResponse => {
    if (optimizationResponse) {
      let responseData = optimizationResponse.data || optimizationResponse;

      // Extract summary data (total savings)
      if (responseData?.summary) {
        const totalPotential = responseData.summary.total_potential_savings || 0;
        const totalImplemented = responseData.summary.total_implemented_savings || 0;
        dashboardState.savingsIdentified = totalPotential + totalImplemented;
      }

      // Extract opportunities list
      let opportunitiesData = responseData?.opportunities || [];
      dashboardState.optimizationOpportunities = Array.isArray(opportunitiesData) ? opportunitiesData : [];
      updateDashboardStats();
    }
  }).catch(() => {});

  // Mark loading as false after a short delay (all requests fired)
  dashboardState.loading = false;
}

/**
 * Update dashboard stats in the DOM
 */
function updateDashboardStats() {
  const contractsEl = document.getElementById('stat-active-contracts');
  const invoicesEl = document.getElementById('stat-invoices-processed');
  const renewalsListEl = document.getElementById('dashboard-renewals-list');

  if (contractsEl && dashboardState.activeContracts !== null) {
    contractsEl.textContent = dashboardState.activeContracts.toLocaleString();
    contractsEl.classList.remove('loading');
  }

  if (invoicesEl && dashboardState.invoicesProcessed !== null) {
    invoicesEl.textContent = dashboardState.invoicesProcessed.toLocaleString();
    invoicesEl.classList.remove('loading');
  }

  const discrepanciesEl = document.getElementById('stat-open-discrepancies');
  if (discrepanciesEl) {
    if (dashboardState.openDiscrepancies !== null) {
      discrepanciesEl.textContent = dashboardState.openDiscrepancies.toLocaleString();
    } else {
      discrepanciesEl.textContent = '0';
    }
    discrepanciesEl.classList.remove('loading');
  }

  // Update savings identified
  const savingsEl = document.getElementById('stat-savings-identified');
  if (savingsEl) {
    if (dashboardState.savingsIdentified !== null) {
      savingsEl.textContent = formatCurrency(dashboardState.savingsIdentified);
    } else {
      savingsEl.textContent = formatCurrency(0);
    }
    savingsEl.classList.remove('loading');
  }

  // Update renewals list
  if (renewalsListEl) {
    const renewals = dashboardState.renewals.slice(0, 4);
    if (renewals.length > 0) {
      renewalsListEl.innerHTML = renewals.map(r => {
        const daysUntil = r.daysUntilExpiration || r.daysUntilRenewal || 0;
        const urgency = daysUntil <= 14 ? 'urgent' : daysUntil <= 30 ? 'soon' : 'upcoming';
        const vendor = r.vendorName || r.vendor || 'Unknown Vendor';
        const title = r.title || r.contractTitle || 'Untitled Contract';
        const value = r.totalValue || r.value || 0;

        return `
          <div class="renewal-item ${urgency}">
            <div class="renewal-status"></div>
            <div class="renewal-info">
              <span class="renewal-vendor">${vendor}</span>
              <span class="renewal-contract">${title}</span>
            </div>
            <div class="renewal-meta">
              <span class="renewal-days">${daysUntil} ${t('common.days')}</span>
              <span class="renewal-value">${formatCurrency(value)}/${t('common.year')}</span>
            </div>
          </div>
        `;
      }).join('');
    } else {
      renewalsListEl.innerHTML = `
        <div class="empty-state">
          <p>${t('renewals.noUpcoming') || 'No upcoming renewals'}</p>
        </div>
      `;
    }
  }

  // Update activities list
  const activitiesListEl = document.getElementById('dashboard-activities-list');
  if (activitiesListEl) {
    const activities = dashboardState.activities.slice(0, 5);
    if (activities.length > 0) {
      activitiesListEl.innerHTML = activities.map(a => `
        <div class="activity-item">
          <div class="activity-icon ${a.type}">
            ${getActivityIcon(a.type)}
          </div>
          <div class="activity-info">
            <span class="activity-title">${a.title}</span>
            <span class="activity-detail">${a.detail}</span>
          </div>
          <span class="activity-time">${a.time}</span>
        </div>
      `).join('');
    } else {
      activitiesListEl.innerHTML = `
        <div class="empty-state">
          <p>${t('dashboard.noRecentActivity') || 'No recent activity'}</p>
        </div>
      `;
    }
  }

  // Update discrepancies list
  const discrepanciesListEl = document.getElementById('dashboard-discrepancies-list');
  if (discrepanciesListEl) {
    const discrepancies = dashboardState.discrepancies.slice(0, 3);
    if (discrepancies.length > 0) {
      discrepanciesListEl.innerHTML = discrepancies.map(d => {
        // Map API field names to display values
        const discType = d.discrepancyType || d.type || 'pricing';
        const impact = d.totalImpact || d.impact || 0;
        const description = d.description || '';
        const invoiceId = d.invoiceNumber || d.invoiceId || '';
        const contractId = d.contractId || '';

        return `
          <div class="discrepancy-item" data-discrepancy-id="${d.id}">
            <div class="discrepancy-type ${discType}">${discType.replace('_', ' ')}</div>
            <div class="discrepancy-info">
              <span class="discrepancy-title">${description || t('discrepancy.overcharge')}</span>
              <span class="discrepancy-detail">${t('common.invoice')} ${invoiceId}${contractId ? ` vs ${t('common.contract')} ${contractId}` : ''}</span>
            </div>
            <span class="discrepancy-impact">+${formatCurrency(Math.abs(impact))}</span>
          </div>
        `;
      }).join('');
    } else {
      discrepanciesListEl.innerHTML = `
        <div class="empty-state">
          <p>${t('discrepancy.noDiscrepancies') || 'No discrepancies found'}</p>
        </div>
      `;
    }
  }

  // Update optimization opportunities
  const optimizationTotalEl = document.getElementById('dashboard-optimization-total');
  const optimizationListEl = document.getElementById('dashboard-optimization-list');

  if (optimizationTotalEl) {
    const totalSavings = dashboardState.savingsIdentified || 0;
    optimizationTotalEl.textContent = `${formatCurrency(totalSavings)}/${t('common.year')}`;
  }

  if (optimizationListEl) {
    const opportunities = dashboardState.optimizationOpportunities.slice(0, 2);
    if (opportunities.length > 0) {
      optimizationListEl.innerHTML = opportunities.map(o => {
        const type = o.type || 'consolidation';
        const title = o.title || '';
        const summary = o.summary || o.description || '';
        const annualSavings = o.details?.estimated_savings?.annual || 0;

        return `
          <div class="optimization-item">
            <div class="optimization-icon ${type}">
              ${getOptimizationIcon(type)}
            </div>
            <div class="optimization-info">
              <span class="optimization-title">${title}</span>
              <span class="optimization-detail">${summary}</span>
            </div>
            <span class="optimization-savings">~${formatCurrency(annualSavings)}/${t('common.year')}</span>
          </div>
        `;
      }).join('');
    } else {
      optimizationListEl.innerHTML = `
        <div class="empty-state">
          <p>${t('optimization.noOpportunities') || 'No optimization opportunities found'}</p>
        </div>
      `;
    }
  }
}

/**
 * Format currency
 */
function formatCurrency(amount) {
  const lang = getCurrentLanguage();
  const currency = getCurrency();
  const locale = lang === 'ar' ? 'ar-SA' : lang === 'fr' ? 'fr-FR' : 'en-US';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
}

/**
 * Format date
 */
function formatDate(dateStr) {
  const lang = getCurrentLanguage();
  const locale = lang === 'ar' ? 'ar-SA' : lang === 'fr' ? 'fr-FR' : 'en-US';
  return new Date(dateStr).toLocaleDateString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

/**
 * Dashboard Page
 */
export function renderDashboard() {
  // Reset state and trigger fetch immediately
  dashboardState.loading = true;
  dashboardState.activeContracts = null;
  dashboardState.invoicesProcessed = null;

  // Fetch real data asynchronously (no delay - starts immediately)
  fetchDashboardStats();

  return `
    <!-- Stats Cards -->
    <section class="stats-grid">
      <div class="stat-card">
        <div class="stat-icon contracts">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
          </svg>
        </div>
        <div class="stat-info">
          <span class="stat-value loading" id="stat-active-contracts">
            <span class="stat-loading-dot"></span>
          </span>
          <span class="stat-label">${t('dashboard.activeContracts')}</span>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon invoices">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <line x1="12" y1="18" x2="12" y2="12"/>
            <line x1="9" y1="15" x2="15" y2="15"/>
          </svg>
        </div>
        <div class="stat-info">
          <span class="stat-value loading" id="stat-invoices-processed">
            <span class="stat-loading-dot"></span>
          </span>
          <span class="stat-label">${t('dashboard.invoicesProcessed')}</span>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon discrepancies">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/>
            <line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
        </div>
        <div class="stat-info">
          <span class="stat-value loading" id="stat-open-discrepancies">
            <span class="stat-loading-dot"></span>
          </span>
          <span class="stat-label">${t('dashboard.openDiscrepancies')}</span>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon savings">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="12" y1="1" x2="12" y2="23"/>
            <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
          </svg>
        </div>
        <div class="stat-info">
          <span class="stat-value loading" id="stat-savings-identified">
            <span class="stat-loading-dot"></span>
          </span>
          <span class="stat-label">${t('dashboard.savingsIdentified')}</span>
        </div>
      </div>
    </section>

    <!-- Main Dashboard Grid -->
    <div class="dashboard-grid">
      <!-- Upcoming Renewals -->
      <section class="card renewals-card">
        <div class="card-header">
          <h2 class="card-title">${t('dashboard.upcomingRenewals')}</h2>
          <a href="#renewals" class="card-link">${t('common.viewAll')}</a>
        </div>
        <div class="card-body">
          <div class="renewal-list" id="dashboard-renewals-list">
            <div class="loading-placeholder">
              <div class="loading-spinner-small"></div>
              <span>${t('common.loading') || 'Loading...'}</span>
            </div>
          </div>
        </div>
      </section>

      <!-- Recent Discrepancies -->
      <section class="card discrepancies-card">
        <div class="card-header">
          <h2 class="card-title">${t('dashboard.recentDiscrepancies')}</h2>
          <a href="#analysis" class="card-link">${t('common.viewAll')}</a>
        </div>
        <div class="card-body">
          <div class="discrepancy-list" id="dashboard-discrepancies-list">
            <div class="loading-placeholder">
              <div class="loading-spinner-small"></div>
              <span>${t('common.loading') || 'Loading...'}</span>
            </div>
          </div>
        </div>
      </section>

      <!-- Recent Activity -->
      <section class="card activity-card">
        <div class="card-header">
          <h2 class="card-title">${t('dashboard.recentActivity')}</h2>
        </div>
        <div class="card-body">
          <div class="activity-list" id="dashboard-activities-list">
            <div class="loading-placeholder">
              <div class="loading-spinner-small"></div>
              <span>${t('common.loading') || 'Loading...'}</span>
            </div>
          </div>
        </div>
      </section>

      <!-- Optimization Opportunities -->
      <section class="card optimization-card">
        <div class="card-header">
          <h2 class="card-title">${t('dashboard.optimizationOpportunities')}</h2>
          <a href="#optimization" class="card-link">${t('common.viewAll')}</a>
        </div>
        <div class="card-body">
          <div class="optimization-summary">
            <span class="optimization-summary-label">${t('optimization.totalSavings')}</span>
            <span class="optimization-summary-value" id="dashboard-optimization-total">
              <span class="stat-loading-dot"></span>
            </span>
          </div>
          <div class="optimization-list" id="dashboard-optimization-list">
            <div class="loading-placeholder">
              <div class="loading-spinner-small"></div>
              <span>${t('common.loading') || 'Loading...'}</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  `;
}

/**
 * Contracts Page (Legacy - redirects to module-based page)
 */
export function renderContracts() {
  // This function is deprecated - the router uses /pages/contracts/index.js instead
  window.location.hash = '#/contracts';
  return `<div class="loading">${t('common.loading')}</div>`;
}

/**
 * Invoices Page (Legacy - redirects to module-based page)
 */
export function renderInvoices() {
  // This function is deprecated - the router uses /pages/invoices/index.js instead
  window.location.hash = '#/invoices';
  return `<div class="loading">${t('common.loading')}</div>`;
}

/**
 * Renewals Page (Legacy - redirects to module-based page)
 */
export function renderRenewals() {
  // This function is deprecated - the router uses /pages/renewals/index.js instead
  window.location.hash = '#/renewals';
  return `<div class="loading">${t('common.loading')}</div>`;
}

/**
 * Analysis Page (Legacy - redirects to module-based page)
 */
export function renderAnalysis() {
  // This function is deprecated - the router uses /pages/analysis/index.js instead
  window.location.hash = '#/analysis';
  return `<div class="loading">${t('common.loading')}</div>`;
}

/**
 * Integrations Page (Legacy - redirects to module-based page)
 */
export function renderIntegrations() {
  // This function is deprecated - the router uses /pages/integrations/index.js instead
  window.location.hash = '#/integrations';
  return `<div class="loading">${t('common.loading')}</div>`;
}

/**
 * Settings Page
 */
export function renderSettings() {
  return `
    <div class="settings-layout">
      <nav class="settings-nav">
        <button class="settings-nav-item active" data-settings="profile">${t('settings.profile')}</button>
        <button class="settings-nav-item" data-settings="notifications">${t('settings.notifications')}</button>
        <button class="settings-nav-item" data-settings="preferences">${t('settings.preferences')}</button>
        <button class="settings-nav-item" data-settings="team">${t('settings.team')}</button>
        <button class="settings-nav-item" data-settings="security">${t('settings.security')}</button>
      </nav>

      <div class="settings-content">
        <div class="settings-section">
          <h3 class="settings-section-title">${t('settings.profileInformation')}</h3>
          <p class="settings-section-desc">${t('settings.updateAccountDetails')}</p>
          
          <div class="form-group">
            <label class="form-label">${t('settings.fullName')}</label>
            <input type="text" class="form-input" value="Jane Doe">
          </div>
          <div class="form-group">
            <label class="form-label">${t('settings.email')}</label>
            <input type="email" class="form-input" value="jane.doe@company.com">
          </div>
          <div class="form-group">
            <label class="form-label">${t('settings.role')}</label>
            <select class="form-input">
              <option selected>${t('settings.procurementManager')}</option>
              <option>${t('settings.complianceOfficer')}</option>
              <option>${t('settings.admin')}</option>
              <option>${t('settings.viewer')}</option>
            </select>
          </div>
        </div>

        <div class="settings-section">
          <h3 class="settings-section-title">${t('settings.notificationPreferences')}</h3>
          <p class="settings-section-desc">${t('settings.chooseNotifications')}</p>
          
          <div class="settings-row">
            <div class="settings-row-info">
              <div class="settings-row-label">${t('settings.emailNotifications')}</div>
              <div class="settings-row-desc">${t('settings.emailNotificationsDesc')}</div>
            </div>
            <label class="toggle">
              <input type="checkbox" checked>
              <span class="toggle-slider"></span>
            </label>
          </div>
          <div class="settings-row">
            <div class="settings-row-info">
              <div class="settings-row-label">${t('settings.renewalReminders')}</div>
              <div class="settings-row-desc">${t('settings.renewalRemindersDesc')}</div>
            </div>
            <label class="toggle">
              <input type="checkbox" checked>
              <span class="toggle-slider"></span>
            </label>
          </div>
          <div class="settings-row">
            <div class="settings-row-info">
              <div class="settings-row-label">${t('settings.discrepancyAlerts')}</div>
              <div class="settings-row-desc">${t('settings.discrepancyAlertsDesc')}</div>
            </div>
            <label class="toggle">
              <input type="checkbox" checked>
              <span class="toggle-slider"></span>
            </label>
          </div>
        </div>

        <div class="settings-section">
          <h3 class="settings-section-title">${t('settings.preferences')}</h3>
          <p class="settings-section-desc">${t('settings.currencyDesc')}</p>

          <div class="form-group">
            <label class="form-label">${t('settings.currency')}</label>
            <select class="form-input" id="currencySelect" onchange="window.setCurrency(this.value)">
              <optgroup label="Major Currencies">
                <option value="EUR" ${getCurrency() === 'EUR' ? 'selected' : ''}>${t('settings.currencyOptions.EUR')}</option>
                <option value="USD" ${getCurrency() === 'USD' ? 'selected' : ''}>${t('settings.currencyOptions.USD')}</option>
                <option value="GBP" ${getCurrency() === 'GBP' ? 'selected' : ''}>${t('settings.currencyOptions.GBP')}</option>
                <option value="JPY" ${getCurrency() === 'JPY' ? 'selected' : ''}>${t('settings.currencyOptions.JPY')}</option>
                <option value="CHF" ${getCurrency() === 'CHF' ? 'selected' : ''}>${t('settings.currencyOptions.CHF')}</option>
                <option value="CAD" ${getCurrency() === 'CAD' ? 'selected' : ''}>${t('settings.currencyOptions.CAD')}</option>
                <option value="AUD" ${getCurrency() === 'AUD' ? 'selected' : ''}>${t('settings.currencyOptions.AUD')}</option>
                <option value="NZD" ${getCurrency() === 'NZD' ? 'selected' : ''}>${t('settings.currencyOptions.NZD')}</option>
              </optgroup>
              <optgroup label="Asia Pacific">
                <option value="CNY" ${getCurrency() === 'CNY' ? 'selected' : ''}>${t('settings.currencyOptions.CNY')}</option>
                <option value="INR" ${getCurrency() === 'INR' ? 'selected' : ''}>${t('settings.currencyOptions.INR')}</option>
                <option value="SGD" ${getCurrency() === 'SGD' ? 'selected' : ''}>${t('settings.currencyOptions.SGD')}</option>
                <option value="HKD" ${getCurrency() === 'HKD' ? 'selected' : ''}>${t('settings.currencyOptions.HKD')}</option>
                <option value="KRW" ${getCurrency() === 'KRW' ? 'selected' : ''}>${t('settings.currencyOptions.KRW')}</option>
                <option value="THB" ${getCurrency() === 'THB' ? 'selected' : ''}>${t('settings.currencyOptions.THB')}</option>
                <option value="MYR" ${getCurrency() === 'MYR' ? 'selected' : ''}>${t('settings.currencyOptions.MYR')}</option>
                <option value="IDR" ${getCurrency() === 'IDR' ? 'selected' : ''}>${t('settings.currencyOptions.IDR')}</option>
                <option value="PHP" ${getCurrency() === 'PHP' ? 'selected' : ''}>${t('settings.currencyOptions.PHP')}</option>
                <option value="VND" ${getCurrency() === 'VND' ? 'selected' : ''}>${t('settings.currencyOptions.VND')}</option>
                <option value="TWD" ${getCurrency() === 'TWD' ? 'selected' : ''}>${t('settings.currencyOptions.TWD')}</option>
              </optgroup>
              <optgroup label="Middle East & North Africa">
                <option value="MAD" ${getCurrency() === 'MAD' ? 'selected' : ''}>${t('settings.currencyOptions.MAD')}</option>
                <option value="SAR" ${getCurrency() === 'SAR' ? 'selected' : ''}>${t('settings.currencyOptions.SAR')}</option>
                <option value="AED" ${getCurrency() === 'AED' ? 'selected' : ''}>${t('settings.currencyOptions.AED')}</option>
                <option value="TND" ${getCurrency() === 'TND' ? 'selected' : ''}>${t('settings.currencyOptions.TND')}</option>
                <option value="EGP" ${getCurrency() === 'EGP' ? 'selected' : ''}>${t('settings.currencyOptions.EGP')}</option>
                <option value="QAR" ${getCurrency() === 'QAR' ? 'selected' : ''}>${t('settings.currencyOptions.QAR')}</option>
                <option value="KWD" ${getCurrency() === 'KWD' ? 'selected' : ''}>${t('settings.currencyOptions.KWD')}</option>
                <option value="BHD" ${getCurrency() === 'BHD' ? 'selected' : ''}>${t('settings.currencyOptions.BHD')}</option>
                <option value="OMR" ${getCurrency() === 'OMR' ? 'selected' : ''}>${t('settings.currencyOptions.OMR')}</option>
                <option value="JOD" ${getCurrency() === 'JOD' ? 'selected' : ''}>${t('settings.currencyOptions.JOD')}</option>
                <option value="ILS" ${getCurrency() === 'ILS' ? 'selected' : ''}>${t('settings.currencyOptions.ILS')}</option>
              </optgroup>
              <optgroup label="Europe">
                <option value="SEK" ${getCurrency() === 'SEK' ? 'selected' : ''}>${t('settings.currencyOptions.SEK')}</option>
                <option value="NOK" ${getCurrency() === 'NOK' ? 'selected' : ''}>${t('settings.currencyOptions.NOK')}</option>
                <option value="DKK" ${getCurrency() === 'DKK' ? 'selected' : ''}>${t('settings.currencyOptions.DKK')}</option>
                <option value="PLN" ${getCurrency() === 'PLN' ? 'selected' : ''}>${t('settings.currencyOptions.PLN')}</option>
                <option value="CZK" ${getCurrency() === 'CZK' ? 'selected' : ''}>${t('settings.currencyOptions.CZK')}</option>
                <option value="HUF" ${getCurrency() === 'HUF' ? 'selected' : ''}>${t('settings.currencyOptions.HUF')}</option>
                <option value="RON" ${getCurrency() === 'RON' ? 'selected' : ''}>${t('settings.currencyOptions.RON')}</option>
                <option value="TRY" ${getCurrency() === 'TRY' ? 'selected' : ''}>${t('settings.currencyOptions.TRY')}</option>
                <option value="RUB" ${getCurrency() === 'RUB' ? 'selected' : ''}>${t('settings.currencyOptions.RUB')}</option>
                <option value="UAH" ${getCurrency() === 'UAH' ? 'selected' : ''}>${t('settings.currencyOptions.UAH')}</option>
              </optgroup>
              <optgroup label="Americas">
                <option value="MXN" ${getCurrency() === 'MXN' ? 'selected' : ''}>${t('settings.currencyOptions.MXN')}</option>
                <option value="BRL" ${getCurrency() === 'BRL' ? 'selected' : ''}>${t('settings.currencyOptions.BRL')}</option>
                <option value="ARS" ${getCurrency() === 'ARS' ? 'selected' : ''}>${t('settings.currencyOptions.ARS')}</option>
                <option value="CLP" ${getCurrency() === 'CLP' ? 'selected' : ''}>${t('settings.currencyOptions.CLP')}</option>
                <option value="COP" ${getCurrency() === 'COP' ? 'selected' : ''}>${t('settings.currencyOptions.COP')}</option>
                <option value="PEN" ${getCurrency() === 'PEN' ? 'selected' : ''}>${t('settings.currencyOptions.PEN')}</option>
              </optgroup>
              <optgroup label="Africa">
                <option value="ZAR" ${getCurrency() === 'ZAR' ? 'selected' : ''}>${t('settings.currencyOptions.ZAR')}</option>
                <option value="NGN" ${getCurrency() === 'NGN' ? 'selected' : ''}>${t('settings.currencyOptions.NGN')}</option>
                <option value="KES" ${getCurrency() === 'KES' ? 'selected' : ''}>${t('settings.currencyOptions.KES')}</option>
                <option value="GHS" ${getCurrency() === 'GHS' ? 'selected' : ''}>${t('settings.currencyOptions.GHS')}</option>
                <option value="XOF" ${getCurrency() === 'XOF' ? 'selected' : ''}>${t('settings.currencyOptions.XOF')}</option>
                <option value="XAF" ${getCurrency() === 'XAF' ? 'selected' : ''}>${t('settings.currencyOptions.XAF')}</option>
              </optgroup>
            </select>
          </div>
        </div>

        <div class="flex-between">
          <button class="btn btn-secondary">${t('common.cancel')}</button>
          <button class="btn btn-primary">${t('common.saveChanges')}</button>
        </div>
      </div>
    </div>
  `;
}

// Helper functions for icons
function getActivityIcon(type) {
  const icons = {
    upload: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>',
    analysis: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>',
    resolved: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg>',
    contract: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>'
  };
  return icons[type] || icons.contract;
}

function getOptimizationIcon(type) {
  const icons = {
    consolidation: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><line x1="9" y1="3" x2="9" y2="21"/></svg>',
    volume: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>',
    renegotiation: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>'
  };
  return icons[type] || icons.consolidation;
}

// Export contract detail renderer for modal (Legacy - redirects to detail page)
export function renderContractDetail(contractId) {
  // This function is deprecated - redirect to the proper contract detail page
  window.location.hash = `#/contracts/${contractId}`;
  return `<div class="loading">${t('common.loading')}</div>`;
}

// Export invoice detail renderer for modal (Legacy - redirects to detail page)
export function renderInvoiceDetail(invoiceId) {
  // This function is deprecated - redirect to the proper invoice detail page
  window.location.hash = `#/invoices/${invoiceId}`;
  return `<div class="loading">${t('common.loading')}</div>`;
}

